import { Modbus as ModbusSdk, Site as SiteSdk } from "coolremote-sdk";
import useMutation from "./useMutation";
import useQuery from "./useQuery";
import { IModbusFunctionalTemplate } from "../screens/Modbus/types";
import { IUITemplate } from "../screens/Modbus/UITemplateMapper";

/** Backend docs carry `_id.$oid` / nested `$oid` refs — flatten to plain `id`. */
const oid = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.$oid ?? v?._id?.$oid ?? undefined;

/** BE stores `defaultUiTemplate` as a JSON-encoded string, not an object. */
const parseUiTemplate = (v: any): IUITemplate | undefined => {
  if (!v || typeof v !== "string") return undefined;
  try {
    return JSON.parse(v);
  } catch {
    return undefined;
  }
};

export const normalizeFunctionalTemplate = (doc: any): IModbusFunctionalTemplate => ({
  ...doc,
  id: oid(doc?._id) ?? doc?.id,
  site: oid(doc?.site),
  defaultUiTemplate: parseUiTemplate(doc?.defaultUiTemplate),
});

/**
 * BE validators are strict Joi schemas (unknown keys rejected). Send only the
 * permitted fields. Create allows `site`; update does not.
 * (validators/modbusFunctionalTemplates.js)
 */
const serializeCreate = (t: IModbusFunctionalTemplate) => ({
  site: t.site,
  name: t.name,
  defaultUiTemplate: t.defaultUiTemplate ? JSON.stringify(t.defaultUiTemplate) : undefined,
  category: t.category,
  rows: t.rows,
});
const serializeUpdate = (t: IModbusFunctionalTemplate) => ({
  name: t.name,
  defaultUiTemplate: t.defaultUiTemplate ? JSON.stringify(t.defaultUiTemplate) : undefined,
  category: t.category,
  rows: t.rows,
});

export const useGetSiteModbusFunctionalTemplates = (siteId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ["siteModbusFunctionalTemplates", siteId],
    queryFn: async (): Promise<IModbusFunctionalTemplate[]> => {
      const res = await SiteSdk.getSiteModbusFunctionalTemplates(siteId);
      const dict = res?.data ?? res ?? {};
      return Object.values(dict).map(normalizeFunctionalTemplate);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: enabled && !!siteId,
  });
};

export const useGetModbusFunctionalTemplate = (templateId: string | undefined) => {
  return useQuery({
    queryKey: ["modbusFunctionalTemplate", templateId],
    queryFn: async (): Promise<IModbusFunctionalTemplate> => {
      const res = await ModbusSdk.getFunctionalTemplateById(templateId as string);
      return normalizeFunctionalTemplate(res?.data ?? res);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: !!templateId,
  });
};

interface IMutationOptions {
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export const useCreateModbusFunctionalTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: IModbusFunctionalTemplate) => {
      const res = await ModbusSdk.createFunctionalTemplate(serializeCreate(data));
      return normalizeFunctionalTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useUpdateModbusFunctionalTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: IModbusFunctionalTemplate }) => {
      const res = await ModbusSdk.updateFunctionalTemplate(templateId, serializeUpdate(data));
      return normalizeFunctionalTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useRenameModbusFunctionalTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) => {
      const res = await ModbusSdk.renameFunctionalTemplate(templateId, name);
      return normalizeFunctionalTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useDeleteModbusFunctionalTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (templateId: string) => {
      // The DELETE body has no `.data`, so the SDK resolves `undefined`. xhr throws
      // on non-2xx, so reaching here means success — return a truthy result.
      await ModbusSdk.deleteFunctionalTemplate(templateId);
      return { success: true };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};
